
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Project } from "@/types";
import { AreaChart, Calendar, Clock, DollarSign, AlertTriangle, Users, ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useNavigate } from "react-router-dom";

interface DashboardStatsProps {
  project: Project;
}

export default function DashboardStats({ project }: DashboardStatsProps) {
  const navigate = useNavigate();
  
  // Calculate overall project progress
  const calculateOverallProgress = () => {
    const totalStages = project.stages.length;
    const completedStages = project.stages.filter((s) => s.status === "completed").length;
    const currentStageProgress = project.stages[project.currentStage]?.progress || 0;
    
    const completeWeight = completedStages / totalStages;
    const currentWeight = 1 / totalStages * (currentStageProgress / 100);
    
    return Math.round((completeWeight + currentWeight) * 100);
  };

  // Calculate days remaining
  const daysRemaining = () => {
    const today = new Date();
    const endDate = project.targetCompletionDate;
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Calculate budget usage
  const budgetPercentage = () => {
    return Math.round((project.actualCost / project.budget) * 100);
  };

  // Count open risks
  const openRisks = project.risks.filter((r) => r.status === "open").length;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card 
        className="cursor-pointer hover:shadow-md transition-all" 
        onClick={() => navigate(`/stage/${project.currentStage}`)}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Project Progress</CardTitle>
          <AreaChart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{calculateOverallProgress()}%</div>
          <Progress value={calculateOverallProgress()} className="mt-2" />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-muted-foreground">
              Stage {project.currentStage}: {project.stages[project.currentStage]?.name}
            </p>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/gantt")}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Time Remaining</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{daysRemaining()} days</div>
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-muted-foreground">
              Target completion: {project.targetCompletionDate.toLocaleDateString()}
            </p>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/reports")}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Budget Used</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{budgetPercentage()}%</div>
          <Progress value={budgetPercentage()} className="mt-2" />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-muted-foreground">
              £{project.actualCost.toLocaleString()} of £{project.budget.toLocaleString()}
            </p>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/risks")}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Risks</CardTitle>
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{openRisks}</div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center">
              <div className="h-2 w-2 bg-red-500 rounded-full mr-1"></div>
              <span className="text-xs text-muted-foreground">
                {project.risks.filter(r => r.probability >= 4 && r.impact >= 4).length} critical
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/activities")}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Outstanding Actions</CardTitle>
          <Calendar className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">12</div>
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center">
              <div className="h-2 w-2 bg-amber-500 rounded-full mr-1"></div>
              <span className="text-xs text-muted-foreground">
                4 requiring attention
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className="cursor-pointer hover:shadow-md transition-all"
        onClick={() => navigate("/information-exchange")}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Team Members</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{project.team.length}</div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-muted-foreground">
              From {new Set(project.team.map(member => member.organisation)).size} organisations
            </p>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
